import type {
  GetApiPayloadByUrl,
  GetApiResponseByUrl,
} from '@crossbeam/openapi';

import {
  getOperatorLabel,
  getOperatorLabelAnyType,
} from '@/constants/filter-operators';
import type { useTeamStore } from '@/stores/TeamStore';
import type {
  FilterFieldOption,
  FilterRowState,
  ReportFilterOperatorTypes,
} from '@/types/filters';
import { ALL_SEGMENTS, SEGMENTS_TO_LABEL_MAP } from '@/types/populations';

export type Agent = GetApiResponseByUrl<'/v0.2/agents'>['data'][number];

type CreatedByUser = ReturnType<ReturnType<typeof useTeamStore>['getUserById']>;
export type EnrichedAgent = Agent & { createdByUser: CreatedByUser };

export type AgentNotificationChannel = NonNullable<
  Agent['notification_channel']
>;

export function getAgentNotificationChannels(agent: {
  notification_channel?: AgentNotificationChannel | null;
}): AgentNotificationChannel[] {
  return agent.notification_channel ? [agent.notification_channel] : [];
}

const DISABLED_REASON_TEMPLATES: readonly { label: string; text: string }[] = [
  {
    label: 'channel not found',
    text: "Couldn't send to {channel} because the Slack channel no longer exists. Pick a new channel.",
  },
  {
    label: 'recipient not found',
    text: "Couldn't send to {channel} because the Slack recipient no longer exists.",
  },
  {
    label: 'channel is archived',
    text: "Couldn't send to {channel} because the Slack channel is archived. Choose an active channel.",
  },
  {
    label: 'missing Slack scope',
    text: 'Crossbeam is missing a Slack permission needed to send to {channel}. Reconnect Slack.',
  },
  {
    label: 'Slack workspace inactive',
    text: "Couldn't send to {channel} because the Slack workspace is inactive.",
  },
  {
    label: 'Slack token expired',
    text: "Crossbeam's Slack connection expired. Reconnect Slack to resume sending to {channel}.",
  },
  {
    label: 'Slack authorization invalid',
    text: "Crossbeam's Slack authorization is no longer valid. Reconnect Slack.",
  },
  {
    label: 'Slack authorization missing',
    text: "Crossbeam isn't authorized to post to Slack. Reconnect Slack.",
  },
];

export function agentDisabledReasonText(
  reason: string | null | undefined,
): string {
  if (!reason) return '';
  const segments = reason.replace(/^undeliverable:\s*/, '').split('; ');
  const humanized = segments.map((segment) => {
    const template = DISABLED_REASON_TEMPLATES.find(({ label }) =>
      segment.endsWith(label),
    );
    if (!template) return segment;
    const channel =
      segment.slice(0, -template.label.length).trim() || 'the Slack channel';
    return template.text.replace('{channel}', channel);
  });
  return humanized.join(' ');
}

export type CreateAgentPayload = GetApiPayloadByUrl<'/v0.2/agents', 'post'>;

export type UpdateAgentPayload = GetApiPayloadByUrl<
  '/v0.2/agents/{id}',
  'patch'
>;

export type TestAgentPayload = GetApiPayloadByUrl<'/v0.2/agents-test', 'post'>;

export type AgentActionType =
  | 'slack_channel'
  | 'slack_channel_partner'
  | 'slack_dm';

export type SlackUser =
  GetApiResponseByUrl<'/v0.1/slack-app/users'>['users'][number];

export type AgentCondition = Agent['conditions'][number];

export const UI_TO_API_OP: Partial<
  Record<ReportFilterOperatorTypes, AgentCondition['op']>
> = {
  in: '=',
  'not in': '!=',
};

export const API_TO_UI_OP: Partial<
  Record<AgentCondition['op'], ReportFilterOperatorTypes>
> = {
  '=': 'in',
  '!=': 'not in',
};

export const PARTNER_POPULATION_SEGMENTS_FIELD: FilterFieldOption<string> = {
  label: 'Partner population',
  value: 'partner_population_segments',
  dataType: 'string',
  initialOptionsExist: true,
  preserveOptionOrder: true,
  options: ALL_SEGMENTS.map((segment) => ({
    label: SEGMENTS_TO_LABEL_MAP[segment],
    value: segment,
  })),
  allowedOperators: ['in'],
};

export const PARTNER_SCORE_LEVEL_FIELD: FilterFieldOption<string> = {
  label: 'Partner score level',
  value: 'partner_score_bucket',
  dataType: 'string',
  initialOptionsExist: true,
  preserveOptionOrder: true,
  options: [
    { label: 'High', value: 'high' },
    { label: 'Medium', value: 'medium' },
    { label: 'Low', value: 'low' },
    { label: 'None', value: 'none' },
    { label: 'Unknown', value: 'unknown' },
  ],
  allowedOperators: ['in', 'not in'],
};

export type AgentFormState = Pick<
  CreateAgentPayload,
  'name' | 'message_template'
> & {
  trigger_type?: TriggerType;
  notification_channel_type?: AgentActionType;
  notification_channel: AgentNotificationChannel | null;
  conditions: ConditionRow[];
};

export const EMPTY_AGENT_FORM_STATE: AgentFormState = {
  name: '',
  trigger_type: undefined,
  notification_channel_type: undefined,
  notification_channel: null,
  message_template: '',
  conditions: [],
};

export type AgentRegistryResponse =
  GetApiResponseByUrl<'/v0.2/agents-registry'>;

export type AgentRegistryTrigger =
  AgentRegistryResponse['data']['triggers'][number];

export type AgentTriggerFilter = AgentRegistryTrigger['filters'][number];

export type TriggerType = AgentRegistryTrigger['trigger_type'];

export const TRIGGER_OPTIONS = [
  { value: 'new_mutual_customer', label: 'New mutual customer' },
  { value: 'new_shared_account', label: 'New mutual account' },
  {
    value: 'partner_opened_opp',
    label: 'Partner opened a deal on a mutual account',
  },
  {
    value: 'partner_won_competing_deal',
    label: 'Partner closes a deal won on an account you have as an active opp',
  },
  {
    value: 'partner_won_shared_deal',
    label: 'Partner won a deal on a mutual account',
  },
] as const satisfies readonly {
  value: TriggerType;
  label: string;
  isHidden?: boolean;
}[];

export const TRIGGER_TYPE_LABELS = Object.fromEntries(
  TRIGGER_OPTIONS.map((item) => [item.value, item.label]),
) as Partial<Record<TriggerType, string>>;

export function triggerLabel(triggerType: TriggerType): string {
  return (
    TRIGGER_TYPE_LABELS[triggerType] ??
    triggerType.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
  );
}

export const ACTION_OPTIONS = [
  { value: 'slack_channel', label: 'Send Slack message to channel' },
  {
    value: 'slack_channel_partner',
    label: 'Send Slack message to partner (Slack Connect)',
  },
  { value: 'slack_dm', label: 'Send direct message' },
] as const satisfies readonly {
  value: AgentActionType;
  label: string;
}[];

export type TemplatePrefill = {
  trigger_type: TriggerType;
  name?: string;
};

export const TEMPLATE_PREFILLS: Record<string, TemplatePrefill> = {
  'We share a new customer': {
    trigger_type: 'new_mutual_customer',
    name: 'We share a new customer',
  },
};

export const TIER_DOWNGRADED_REASON = 'tier_downgraded';

export type CellAction = 'edit' | 'delete';

export type AgentsTableContext = {
  actionTaken: (action: CellAction, ctx: string) => void;
};

export type ConditionRow = {
  id: string;
  state: FilterRowState<string, unknown>;
};

export function resolveConditionFieldOptions(
  triggerType: TriggerType | undefined,
  conditionFields: Iterable<string | undefined>,
  fieldOptionsByTriggerType: Partial<
    Record<TriggerType, FilterFieldOption<string>[]>
  >,
  fallbackFieldOptionByField: Map<string, FilterFieldOption<string>>,
): FilterFieldOption<string>[] {
  if (!triggerType) return [];
  const registryOptions = fieldOptionsByTriggerType[triggerType] ?? [];

  const offered = new Set(registryOptions.map((option) => option.value));
  const fallbacks = [...new Set(conditionFields)]
    .filter((field): field is string => !!field && !offered.has(field))
    .map((field) => fallbackFieldOptionByField.get(field))
    .filter((option): option is FilterFieldOption<string> => !!option);
  return [...registryOptions, ...fallbacks];
}

export function formatCondition(
  condition: AgentCondition,
  fieldOptions: FilterFieldOption<string>[],
): string {
  const fieldOption = fieldOptions.find((o) => o.value === condition.field);
  const fieldLabel = fieldOption?.label ?? condition.field;
  const uiOp = API_TO_UI_OP[condition.op] ?? condition.op;
  const opLabel = fieldOption?.dataType
    ? getOperatorLabel(fieldOption.dataType, uiOp)
    : getOperatorLabelAnyType(uiOp);
  const rawValues = Array.isArray(condition.value)
    ? condition.value
    : condition.value != null
      ? [condition.value]
      : [];
  const valueLabels = rawValues
    .map((v) => {
      const opt = fieldOption?.options?.find(
        (o) => String(o.value) === String(v),
      );
      return opt?.label ?? String(v);
    })
    .join(', ');
  return `${fieldLabel} ${opLabel} ${valueLabels}`.trimEnd();
}
